import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  Chip,
  Card,
  TextField,
  Grid,
  useMediaQuery
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassFullIcon from '@mui/icons-material/HourglassFull';
import ErrorIcon from '@mui/icons-material/Error';
import OrderDetails from './OrderDetails';
import translation from '../translations/salesPromotionsTableTranslation';
import { BRAND, BRAND_DARK } from '../constants/salesPromotionsTheme';

const SalesPromotionsTable = ({
  salesPromotions,
  product_columns,
  columns,
  highlightOrderId, // new param to highlight a specific order
  onEditOrder, // function to edit an order
  onDuplicateOrder, // function to duplicate an order
  isAdminView = false // admin view mode
}) => {
  const [filter, setFilter] = useState({
    ordName: '',
    date: '',
  });
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('md'));

  const filteredPromotions = useMemo(() => {
    return salesPromotions.filter((sp) => {
      const ordName = sp?.ordName || '';
      const date = sp?.createdAt || '';
      const spNameMatch = ordName.toLowerCase().includes(filter.ordName.toLowerCase());
      const dateMatch = date.includes(filter.date);
      return spNameMatch && dateMatch;
    });
  }, [salesPromotions, filter]);

  const displayedPromotions = useMemo(() => {
    // if highlightOrderId is set, show only that order
    if (highlightOrderId) {
      return salesPromotions.filter(sp => sp.ordName === highlightOrderId);
    }

    if (filter.ordName || filter.date) {
      return filteredPromotions;
    }
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    return salesPromotions.filter(sp =>
      new Date(sp.createdAt) >= oneYearAgo
    );
  }, [filteredPromotions, filter, highlightOrderId, salesPromotions]);

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2, md: 3 },
      width: '100%',
      maxWidth: '100vw',
      overflow: 'hidden'
    }}>
      <Typography
        variant="h1"
        gutterBottom
        sx={{
          color: isAdminView ? BRAND_DARK : BRAND,
          fontWeight: "bold",
          fontSize: { xs: "1.25rem", sm: "1.5rem" },
          margin: { xs: '8px', sm: '10px' },
          textAlign: 'left'
        }}
      >
        {highlightOrderId ? `הזמנה ${highlightOrderId}` : isAdminView ? translation.allOrdersTitle : "רשימת ההזמנות שלך"}
      </Typography>

      {/* הסתר את הפילטרים במצב צפייה בהזמנה ספציפית */}
      {!highlightOrderId && (
        <Box sx={{
          mb: { xs: 2, md: 3 },
          width: '100%',
          px: { xs: 1, sm: 2 }
        }}>
          <Grid container spacing={2}> 
            <Grid item xs={12} sm={6}>
              <TextField
                label={translation.orderNumber}
                variant="outlined"
                size="small"
                value={filter.ordName}
                onChange={(e) => setFilter({ ...filter, ordName: e.target.value })}
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  width: '100%',
                  maxWidth: '300px'
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={translation.date}
                variant="outlined"
                size="small"
                value={filter.date}
                onChange={(e) => setFilter({ ...filter, date: e.target.value })}
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  width: '100%',
                  maxWidth: '300px'
                }}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* הודעה אם לא נמצאה הזמנה */}
      {displayedPromotions.length === 0 && highlightOrderId && (
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          minHeight="200px"
          gap={2}
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            p: 4,
            textAlign: 'center'
          }}
        >
          <Typography variant="h6" color="text.secondary">
            הזמנה {highlightOrderId} לא נמצאה
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ייתכן שההזמנה לא קיימת או שאין לך הרשאה לצפות בה
          </Typography>
        </Box>
      )}

      {/* הודעה אם אין הזמנות כלל */}
      {displayedPromotions.length === 0 && !highlightOrderId && (
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          minHeight="200px"
          gap={2}
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            p: 4,
            textAlign: 'center'
          }}
        >
          <Typography variant="h6" color="text.secondary">
            לא נמצאו הזמנות
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {(filter.ordName || filter.date) ? 
              "נסה לשנות את קריטריוני החיפוש" : 
              "עדיין לא יצרת הזמנות או שההזמנות שלך הן מלפני יותר משנה"}
          </Typography>
        </Box>
      )}

      {displayedPromotions.map((sp, index) => (
        <Card
          key={index}
          sx={{
            mb: { xs: 2, md: 3 },
            borderRadius: { xs: 2, md: 4 },
            overflow: 'visible',
            transition: 'all 0.3s ease',
            // highlight the card if this is the order being searched for
            border: highlightOrderId === sp.ordName ? '3px solid #FF9800' : 'none',
            boxShadow: highlightOrderId === sp.ordName 
              ? '0 4px 20px rgba(255, 152, 0, 0.3)' 
              : {
                  xs: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  md: '0 3px 5px 2px rgba(0, 0, 0, 0.1)'
                },
            '&:hover': {
              md: {
                transform: 'translateY(-5px)',
                boxShadow: highlightOrderId === sp.ordName 
                  ? '0 6px 25px rgba(255, 152, 0, 0.4)'
                  : '0 5px 8px 3px rgba(0, 0, 0, 0.2)',
              }
            },
            width: '100%',
            // subtle animation to highlight the order
            ...(highlightOrderId === sp.ordName && {
              animation: 'highlight-pulse 2s ease-in-out',
              '@keyframes highlight-pulse': {
                '0%': { boxShadow: '0 4px 20px rgba(255, 152, 0, 0.3)' },
                '50%': { boxShadow: '0 8px 30px rgba(255, 152, 0, 0.5)' },
                '100%': { boxShadow: '0 4px 20px rgba(255, 152, 0, 0.3)' }
              }
            })
          }}
        >
          <Accordion 
            // auto-expand the highlighted order
            defaultExpanded={highlightOrderId === sp.ordName}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: '#757575' }} />}
              aria-controls={`panel${index}a-content`}
              id={`panel${index}a-header`}
              sx={{
                flexDirection: 'column',
                '& .MuiAccordionSummary-content': {
                  width: '100%',
                  margin: '8px 0',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 1, md: 2 },
                  alignItems: { xs: 'flex-start', md: 'center' }
                }
              }}
            >
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                width: '100%',
                gap: { xs: 1, md: 2 }
              }}>
                <Box sx={{
                  flexBasis: { md: '33%' },
                  // highlight the title if this is the requested order
                  ...(highlightOrderId === sp.ordName && {
                    color: '#FF9800',
                  })
                }}>
                  <Typography sx={{
                    fontWeight: 500,
                    fontSize: { xs: '1rem', md: '1.1rem' },
                  }}>
                    {sp.deliveryCustomerDescription || sp.nsDesCustomer || sp.selectedRecivingCustomer || sp.selectedOrderingCustomer || ''}
                  </Typography>
                  <Typography sx={{
                    fontSize: { xs: '0.8rem', md: '0.9rem' },
                    fontWeight: 'normal',
                    color: 'text.secondary'
                  }}>
                    {sp.address?.street}{sp.address?.street && sp.address?.city ? ', ' : ''}{sp.address?.city}
                  </Typography>
                </Box>
                
                <Box sx={{ flexGrow: 1 }}>
                  <Typography sx={{
                    color: 'text.secondary',
                    fontSize: { xs: '0.875rem', md: '1rem' },
                  }}>
                    {sp.createdAt.split("T")[0]}
                  </Typography>
                  {isAdminView && sp.email && (
                    <Typography sx={{
                      color: BRAND_DARK,
                      fontSize: { xs: '0.75rem', md: '0.85rem' },
                      fontWeight: 500
                    }}>
                      {translation.createdBy}: {sp.email}
                    </Typography>
                  )}
                </Box>

                {sp.status && <Chip
                  icon={
                    sp.status === "Approval Required" ? <HourglassFullIcon /> :
                    sp.status === "Rejected" ? <ErrorIcon /> :
                    <CheckCircleIcon />
                  }
                  label={
                    sp.status === "Approval Required" ? translation.waitingForApproval :
                    sp.status === "Rejected" ? `${translation.rejectedBy} ${sp?.rejecting?.Email.split("@")[0]}` :
                    sp.confirming ? `${translation.approvedBy} ${sp.confirming?.Email.split("@")[0]}` :
                    translation.createdSuccessfully
                  }
                  color={
                    sp.status === "Approval Required" ? "warning" :
                    sp.status === "Rejected" ? "error" :
                    "success"
                  }
                  size="small"
                  sx={{
                    minWidth: '120px',
                    maxWidth: '100%',
                    height: 'auto',
                    '& .MuiChip-label': {
                      whiteSpace: 'normal',
                      overflow: 'visible',
                      textOverflow: 'clip',
                      py: 0.5
                    }
                  }}
                />}
              </Box>
            </AccordionSummary>
            <OrderDetails
                  sp={sp}
                  product_columns={product_columns}
                  columns={columns}
                  onEditOrder={isAdminView ? undefined : onEditOrder}
                  onDuplicateOrder={isAdminView ? undefined : onDuplicateOrder}
                />
          </Accordion>
        </Card>
      ))}
    </Box>
  );
};

export default SalesPromotionsTable;